/**
 * What the Home overview says, decided from `/home/summary` as pure functions.
 *
 * The page is four short things: Ardi's brief (a few sentences on the last
 * thirty days), what needs the vendor, one row of key numbers, and one line
 * per workspace they use. Each is chosen here — which sentences, which
 * numbers, which workspaces — so the rules can be tested without rendering the
 * page. What comes out is a translation key and its figures; the page says it
 * in the vendor's language.
 */
import type { Compared, HomeSummary } from '../../../common/hooks/use-home-api.hook';

export type OverviewTone = 'good' | 'bad' | 'watch' | 'neutral';

/** How money and counts are written; the page passes the vendor's locale, tests pass something plain. */
export interface Formatters {
    money: (amount: number | null | undefined, currency?: string | null) => string;
    number: (value: number | null | undefined) => string;
}

type Params = Record<string, string | number>;

/** A change of at least this many percent is worth a word; below it, "about the same". */
const NOTABLE = 10;

const changeTone = (change: number | null | undefined, invert = false): OverviewTone => {
    if (change === null || change === undefined || Math.abs(change) < NOTABLE) return 'neutral';
    const up = change > 0;
    return (invert ? !up : up) ? 'good' : 'watch';
};

/** The words after a figure: "12% more than the 30 days before", or nothing when the change is small or unknown. */
const changeDetail = (change: number | null | undefined): { detail?: string; params: Params } => {
    if (change === null || change === undefined || Math.abs(change) < 1) return { params: {} };
    return { detail: change > 0 ? 'BRIEF.UP' : 'BRIEF.DOWN', params: { change: Math.abs(Math.round(change)) } };
};

// ── Ardi's brief ────────────────────────────────────────────────────────────

export interface BriefItem {
    id: string;
    tone: OverviewTone;
    /** Under HOME_OVERVIEW. */
    title: string;
    detail?: string;
    params: Params;
    href?: string;
}

/**
 * A few sentences on the last thirty days, most telling first: who used the
 * visualizer and what came of it, what the assistant did, what sold, what
 * ads brought, what goes out this week. Only what the vendor has — a
 * workspace outside their plan, or one never connected, says nothing.
 */
export function homeBrief(
    summary: HomeSummary | null | undefined,
    { measures = true, format, limit = 5 }: { measures?: boolean; format: Formatters; limit?: number },
): BriefItem[] {
    if (!summary) return [];
    const w = summary.workspaces;
    const items: BriefItem[] = [];

    const visitors = measures ? w.visualizer?.visitors : null;
    if (visitors && (visitors.value ?? 0) > 0) {
        const { detail, params } = changeDetail(visitors.change);
        items.push({
            id: 'visitors',
            tone: changeTone(visitors.change),
            title: 'BRIEF.VISITORS',
            detail,
            params: { ...params, count: visitors.value!, shown: format.number(visitors.value) },
            href: '/visualizer/dashboard',
        });
    }

    const leads = measures ? w.visualizer?.leads : null;
    if (leads && (leads.value ?? 0) > 0) {
        const { detail, params } = changeDetail(leads.change);
        items.push({
            id: 'leads',
            tone: changeTone(leads.change),
            title: 'BRIEF.LEADS',
            detail,
            params: { ...params, count: leads.value!, shown: format.number(leads.value) },
            href: '/visualizer/engagement/leads',
        });
    }

    const assistant = w.assistant && !w.assistant.locked ? w.assistant : null;
    if (assistant && (assistant.conversations ?? 0) > 0) {
        const unanswered = assistant.unanswered ?? 0;
        items.push({
            id: 'assistant',
            tone: unanswered > 0 ? 'watch' : 'good',
            title: 'BRIEF.ASSISTANT',
            detail: unanswered > 0 ? 'BRIEF.ASSISTANT_UNANSWERED' : undefined,
            params: { count: assistant.conversations!, shown: format.number(assistant.conversations), unanswered },
            href: unanswered > 0 ? '/assistant/questions' : '/assistant/dashboard',
        });
    }

    if (w.business?.connected && (w.business.orders ?? 0) > 0) {
        items.push({
            id: 'revenue',
            tone: 'neutral',
            title: 'BRIEF.REVENUE',
            params: { revenue: format.money(w.business.revenue, w.business.currency), count: w.business.orders },
            href: '/business/sales/revenue',
        });
    }

    const ads = w.ads && !w.ads.locked && w.ads.connected ? w.ads : null;
    if (ads && (ads.spend ?? 0) > 0) {
        const cost = ads.costPerLead ?? null;
        items.push({
            id: 'ads',
            tone: (ads.leads ?? 0) > 0 ? 'neutral' : 'watch',
            title: 'BRIEF.ADS',
            detail: cost !== null && (ads.leads ?? 0) > 0 ? 'BRIEF.ADS_COST' : undefined,
            params: { spend: format.money(ads.spend, ads.currency), count: ads.leads ?? 0, cost: format.money(cost, ads.currency) },
            href: '/ads/dashboard',
        });
    }

    const scheduled = w.marketing?.scheduledNext7Days;
    if (typeof scheduled === 'number') {
        if (scheduled > 0) {
            items.push({ id: 'posts', tone: 'good', title: 'BRIEF.POSTS', params: { count: scheduled }, href: '/marketing/social-scheduler' });
        } else if (summary.features.socialScheduler) {
            items.push({ id: 'posts', tone: 'watch', title: 'BRIEF.NO_POSTS', params: {}, href: '/marketing/social-scheduler' });
        }
    }

    const images = w.marketing?.imagesMade;
    if (images && (images.value ?? 0) > 0) {
        items.push({ id: 'images', tone: 'neutral', title: 'BRIEF.IMAGES', params: { count: images.value! }, href: '/marketing/content-studio' });
    }

    if (!items.length) {
        return [{ id: 'quiet', tone: 'neutral', title: 'BRIEF.QUIET', detail: 'BRIEF.QUIET_DETAIL', params: {}, href: '/home/integration' }];
    }
    return items.slice(0, limit);
}

// ── What needs the vendor ───────────────────────────────────────────────────

export interface AttentionEntry {
    key: string;
    tone: OverviewTone;
    /** A key under HOME_OVERVIEW with its figures, a finding for the insight renderer, or a title as written. */
    text: { kind: 'key'; key: string; params: Params } | { kind: 'finding'; finding: any } | { kind: 'raw'; text: string };
    route: string;
}

const SEVERITY_TONE: Record<string, OverviewTone> = { critical: 'bad', warning: 'watch', info: 'neutral' };
const RANK: Record<OverviewTone, number> = { bad: 0, watch: 1, neutral: 2, good: 3 };

/**
 * Everything waiting for the vendor, from every workspace, most urgent first:
 * accounts that stopped answering, posts that failed, the analyst's findings,
 * ad suggestions, the shopper questions nobody could answer. Each points at
 * the page where it is dealt with.
 */
export function attentionItems(summary: HomeSummary | null | undefined, actionRoute?: (finding: any) => string | null | undefined): AttentionEntry[] {
    if (!summary) return [];
    const { attention } = summary;
    const items: AttentionEntry[] = [];

    for (const account of attention.ads?.reconnect ?? []) {
        items.push({
            key: `reauth:${account.accountName}`,
            tone: 'bad',
            text: { kind: 'key', key: 'NEEDS.AD_REAUTH', params: { name: account.accountName } },
            route: '/ads/connections',
        });
    }
    for (const account of attention.social?.expiredAccounts ?? []) {
        items.push({
            key: `social-expired:${account.accountName}`,
            tone: 'bad',
            text: { kind: 'key', key: 'NEEDS.SOCIAL_EXPIRED', params: { name: account.accountName } },
            route: '/marketing/social-scheduler?tab=accounts',
        });
    }
    if (attention.social?.failedPosts) {
        items.push({
            key: 'social-failed',
            tone: 'watch',
            text: { kind: 'key', key: 'NEEDS.POSTS_FAILED', params: { count: attention.social.failedPosts } },
            route: '/marketing/social-scheduler?tab=posts',
        });
    }
    for (const finding of attention.findings ?? []) {
        items.push({
            key: `finding:${finding.findingId}`,
            tone: SEVERITY_TONE[finding.severity] ?? 'neutral',
            text: { kind: 'finding', finding },
            route: actionRoute?.(finding) ?? '/visualizer/dashboard',
        });
    }
    for (const suggestion of attention.ads?.suggestions ?? []) {
        items.push({
            key: `ad:${suggestion.uuid}`,
            tone: SEVERITY_TONE[suggestion.severity] ?? 'neutral',
            text: { kind: 'raw', text: suggestion.title },
            route: `/ads/suggestions?focus=${suggestion.uuid}`,
        });
    }
    if (attention.assistantUnanswered > 0) {
        items.push({
            key: 'assistant-unanswered',
            tone: 'watch',
            text: { kind: 'key', key: 'NEEDS.UNANSWERED', params: { count: attention.assistantUnanswered } },
            route: '/assistant/questions',
        });
    }

    // Stable within a tone, so each source keeps the order it ranked its own items in.
    return items
        .map((item, index) => ({ item, index }))
        .sort((a, b) => RANK[a.item.tone] - RANK[b.item.tone] || a.index - b.index)
        .map(({ item }) => item);
}

// ── Key numbers ─────────────────────────────────────────────────────────────

export interface KeyFigure {
    key: string;
    /** Under HOME_OVERVIEW.FIGURES. */
    label: string;
    value: string;
    delta?: { current: number; previous: number };
    href: string;
}

const deltaOf = (figure: Compared | null | undefined) =>
    figure && typeof figure.value === 'number' && typeof figure.previous === 'number'
        ? { current: figure.value, previous: figure.previous }
        : undefined;

/**
 * One row of numbers across the workspaces — at most five, and only what the
 * vendor has, so a plan without ads or a shop never linked adds no card of
 * dashes. Each opens the page the number comes from.
 */
export function keyFigures(
    summary: HomeSummary | null | undefined,
    { measures = true, format, limit = 5 }: { measures?: boolean; format: Formatters; limit?: number },
): KeyFigure[] {
    if (!summary) return [];
    const w = summary.workspaces;
    const figures: KeyFigure[] = [];

    if (w.visualizer && measures) {
        figures.push(
            { key: 'visitors', label: 'VISITORS', value: format.number(w.visualizer.visitors.value), delta: deltaOf(w.visualizer.visitors), href: '/visualizer/dashboard' },
            { key: 'leads', label: 'LEADS', value: format.number(w.visualizer.leads.value), delta: deltaOf(w.visualizer.leads), href: '/visualizer/engagement/leads' },
        );
    }
    if (w.assistant && !w.assistant.locked) {
        figures.push({ key: 'conversations', label: 'CONVERSATIONS', value: format.number(w.assistant.conversations ?? 0), href: '/assistant/dashboard' });
    }
    if (w.business?.connected) {
        figures.push({ key: 'revenue', label: 'REVENUE', value: format.money(w.business.revenue, w.business.currency), href: '/business/sales/revenue' });
    }
    if (w.ads && !w.ads.locked && w.ads.connected) {
        figures.push({
            key: 'adLeads',
            label: 'AD_LEADS',
            value: format.number(w.ads.leads ?? 0),
            delta: typeof w.ads.previous?.leads === 'number' ? { current: w.ads.leads ?? 0, previous: w.ads.previous.leads } : undefined,
            href: '/ads/dashboard',
        });
    }
    if (w.marketing?.imagesMade) {
        figures.push({ key: 'images', label: 'IMAGES', value: format.number(w.marketing.imagesMade.value), delta: deltaOf(w.marketing.imagesMade), href: '/marketing/dashboard' });
    }
    return figures.slice(0, limit);
}

// ── Workspaces ──────────────────────────────────────────────────────────────

export interface WorkspaceLine {
    id: 'visualizer' | 'assistant' | 'marketing' | 'ads' | 'business';
    /** Under HOME_OVERVIEW.WORKSPACES. */
    line: string;
    params: Params;
    href: string;
}

/**
 * The workspaces the vendor uses, one line each. A workspace their plan does
 * not include is left out: Home is not the place to sell them one.
 */
export function workspaceLines(
    summary: HomeSummary | null | undefined,
    { measures = true, format, goals = [] }: { measures?: boolean; format: Formatters; goals?: readonly string[] },
): WorkspaceLine[] {
    if (!summary) return [];
    const w = summary.workspaces;
    const lines: WorkspaceLine[] = [];

    if (w.visualizer) {
        lines.push(measures
            ? { id: 'visualizer', line: 'VISUALIZER', params: { visitors: format.number(w.visualizer.visitors.value), count: w.visualizer.leads.value ?? 0 }, href: '/visualizer/dashboard' }
            : { id: 'visualizer', line: 'VISUALIZER_QUIET', params: {}, href: '/visualizer/dashboard' });
    }
    if (w.assistant && !w.assistant.locked) {
        lines.push({
            id: 'assistant',
            line: (w.assistant.unanswered ?? 0) > 0 ? 'ASSISTANT_UNANSWERED' : 'ASSISTANT',
            params: { count: w.assistant.conversations ?? 0, unanswered: w.assistant.unanswered ?? 0 },
            href: '/assistant/dashboard',
        });
    }
    if (w.marketing) {
        lines.push({
            id: 'marketing',
            line: 'MARKETING',
            params: { count: w.marketing.scheduledNext7Days ?? 0, images: format.number(w.marketing.imagesMade?.value ?? 0) },
            href: '/marketing/dashboard',
        });
    }
    if (w.ads && !w.ads.locked) {
        lines.push(w.ads.connected
            ? { id: 'ads', line: 'ADS', params: { spend: format.money(w.ads.spend, w.ads.currency), count: w.ads.leads ?? 0 }, href: '/ads/dashboard' }
            : { id: 'ads', line: 'ADS_CONNECT', params: {}, href: '/ads/connections' });
    }
    if (w.business) {
        lines.push(w.business.connected
            ? { id: 'business', line: 'BUSINESS', params: { revenue: format.money(w.business.revenue, w.business.currency), count: w.business.orders }, href: '/business/dashboard' }
            : { id: 'business', line: 'BUSINESS_SEARCH', params: {}, href: '/business/dashboard' });
    }
    // What the vendor said they came for (onboarding) first, in their order;
    // the rest keep the default order after them.
    const rank = (id: string) => {
        const i = goals.indexOf(id);
        return i < 0 ? goals.length + lines.findIndex((l) => l.id === id) : i;
    };
    return goals.length ? [...lines].sort((a, b) => rank(a.id) - rank(b.id)) : lines;
}

// ── Quick actions ───────────────────────────────────────────────────────────

export interface QuickAction {
    key: 'PRODUCT' | 'IMAGE' | 'POST' | 'SEARCH';
    href: string;
}

/** Shortcuts to the things a vendor starts most, minus the ones their plan leaves out. */
export function quickActions(summary: HomeSummary | null | undefined): QuickAction[] {
    const features = summary?.features;
    const actions: QuickAction[] = [{ key: 'PRODUCT', href: '/products/new' }];
    if (!features || features.contentStudio) actions.push({ key: 'IMAGE', href: '/marketing/content-studio' });
    if (!features || features.socialScheduler) actions.push({ key: 'POST', href: '/marketing/social-scheduler/new' });
    actions.push({ key: 'SEARCH', href: '/business/visibility/search' });
    return actions;
}

// ── Last visit ──────────────────────────────────────────────────────────────

const VISIT_KEY = 'ardealy.bo.overview.lastVisit';

/** Opening the page again within this long is the same visit, not a new one. */
const SAME_VISIT_MS = 30 * 60 * 1000;

export interface VisitRecord {
    /** The last time the page was open. */
    seen?: string;
    /** When the visit before this one was — what "since your last visit" means. */
    before?: string;
}

/**
 * The record after opening the page at `now`. A reload or a quick return keeps
 * the previous visit; coming back after half an hour starts a new one.
 */
export function nextVisit(record: VisitRecord | undefined, now: Date): VisitRecord {
    const seen = record?.seen ? new Date(record.seen).getTime() : NaN;
    if (Number.isFinite(seen) && now.getTime() - seen < SAME_VISIT_MS) {
        return { seen: now.toISOString(), before: record?.before };
    }
    return { seen: now.toISOString(), before: Number.isFinite(seen) ? record!.seen : undefined };
}

const visits = (): Record<string, VisitRecord> => {
    try {
        const parsed = JSON.parse(localStorage.getItem(VISIT_KEY) || '{}');
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
};

/**
 * When this person's previous visit to the overview was, noting this one.
 * Per user and in this browser only; a missing or unreadable store simply
 * means "first visit".
 */
export function visitSince(userId: string | undefined, now: Date = new Date()): Date | null {
    if (!userId) return null;
    const all = visits();
    const next = nextVisit(all[userId], now);
    try {
        localStorage.setItem(VISIT_KEY, JSON.stringify({ ...all, [userId]: next }));
    } catch {
        /* next time reads as the first visit */
    }
    const before = next.before ? new Date(next.before) : null;
    return before && !Number.isNaN(before.getTime()) ? before : null;
}
